import React, { ButtonHTMLAttributes, FC, ReactNode } from 'react'
import { Dialog } from '@material-ui/core'
import { useButtonStyles, useDialogStyles } from './styles'
import { formatDate } from './format'

type Tone = 'warning' | 'primary' | 'destructive'

interface PromptDialogProps {
  open: boolean
  labelledBy: string
  describedBy: string
  // A dialog the customer cannot get past is an alert, not a plain dialog.
  blocking?: boolean
  // Stripe's bank window is an iframe outside the dialog; a focus trap would
  // keep pulling focus back, so the customer could not type in it. Set only while it is open.
  allowOutsideFocus?: boolean
}

// Like the mock, Escape and backdrop clicks never close any of these dialogs.
export const PromptDialog: FC<PromptDialogProps> = ({
  open,
  labelledBy,
  describedBy,
  blocking,
  allowOutsideFocus,
  children
}) => {
  const classes = useDialogStyles()
  return (
    <Dialog
      aria-describedby={describedBy}
      aria-labelledby={labelledBy}
      BackdropProps={{ className: classes.backdrop }}
      classes={{ paper: classes.paper }}
      disableBackdropClick
      disableEnforceFocus={allowOutsideFocus}
      disableEscapeKeyDown
      maxWidth={false}
      open={open}
      PaperProps={{ role: blocking ? 'alertdialog' : 'dialog' }}
    >
      {children}
    </Dialog>
  )
}

interface PromptHeaderProps {
  id: string
  descriptionId: string
  icon: ReactNode
  tone: Tone
  title: string
}

// Icon circle, title and centred description (children).
export const PromptHeader: FC<PromptHeaderProps> = ({
  id,
  descriptionId,
  icon,
  tone,
  title,
  children
}) => {
  const classes = useDialogStyles()
  return (
    <div className={classes.header}>
      <div className={`${classes.iconCircle} ${classes[tone]}`}>{icon}</div>
      <h2
        className={classes.title}
        id={id}
      >
        {title}
      </h2>
      <p
        className={classes.description}
        id={descriptionId}
      >
        {children}
      </p>
    </div>
  )
}

export const PromptFooter: FC = ({ children }) => (
  <div className={useDialogStyles().footer}>{children}</div>
)

// Anything we show that the mock does not, placed under the footer so the footer matches the mock.
export const PromptExtras: FC = ({ children }) => (
  <div className={useDialogStyles().extras}>{children}</div>
)

type Variant = 'primary' | 'outline' | 'ghost' | 'text' | 'textPrimary'

interface PromptButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant: Variant
}

export const PromptButton: FC<PromptButtonProps> = ({ variant, className, ...props }) => {
  const classes = useButtonStyles()
  return (
    <button
      className={[classes.base, classes[variant], className].filter(Boolean).join(' ')}
      type='button'
      {...props}
    />
  )
}

// Bordered, lightly shaded box (rounded-lg border bg-muted/40 px-4 py-3).
export const SummaryBox: FC<{ className?: string }> = ({ className, children }) => {
  const classes = useDialogStyles()
  return <div className={[classes.summary, className].filter(Boolean).join(' ')}>{children}</div>
}

export const NextInvoiceLine: FC<{ date?: string | null }> = ({ date }) => {
  const classes = useDialogStyles()
  if (!date) return null
  return (
    <p className={classes.mutedCenter}>
      Next invoice date after payment: <span className={classes.medium}>{formatDate(date)}</span>
    </p>
  )
}
